import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { findActiveModule, getFirstAllowedModulePath } from "@/config/adminModules";

import Header from "@/components/Header";
import PasswordGate from "@/components/PasswordGate";
import ThemeProvider from "@/lib/ThemeProvider";
import AdminLayout from "@/components/admin/AdminLayout";
import ChatbotWidget from "@/components/ChatbotWidget";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Comparator from "@/pages/Comparator";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import SuppliersCompare from "@/pages/SuppliersCompare";
import AdminQuotes from "@/pages/AdminQuotes";
import AdminPartnerOffers from "@/pages/AdminPartnerOffers";
import AdminCEELeads from "@/pages/AdminCEELeads";
import AdminAnalyticsAI from "@/pages/AdminAnalyticsAI";
import AdminAnalyticsGlobal from "@/pages/AdminAnalyticsGlobal";
import AdminMail from "@/pages/AdminMail";
import AdminAccount from "@/pages/AdminAccount";
import AdminAudit from "@/pages/AdminAudit";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminSuppliers from "@/pages/AdminSuppliers";
import AdminUsers from "@/pages/AdminUsers";
import AdminTeam from "@/pages/AdminTeam";
import AdminClients from "@/pages/AdminClients";
import AdminContent from "@/pages/AdminContent";
import AdminWhatsApp from "@/pages/AdminWhatsApp";
import AdminIsolationLeads from "@/pages/AdminIsolationLeads";
import AdminCallbacks from "@/pages/AdminCallbacks";
import AdminContactMessages from "@/pages/AdminContactMessages";
import CEESolaire from "@/pages/CEESolaire";
import QuoteForm from "@/pages/QuoteForm";
import IsolationPAC from "@/pages/IsolationPAC";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NotFound from "@/pages/NotFound";

// --- Site public 2.0 (charte Energy Market) ---
import HeaderV2 from "@/components/v2/Header";
import AudienceGate from "@/components/v2/AudienceGate";
import FooterV2 from "@/components/v2/Footer";
import HomeV2 from "@/pages/v2/Home";
import ProfessionnelsV2 from "@/pages/v2/Professionnels";
import OptimisationContratsV2 from "@/pages/v2/OptimisationContrats";
import RenegociationContratsV2 from "@/pages/v2/RenegociationContrats";
import GestionMultiSitesV2 from "@/pages/v2/GestionMultiSites";
import ParticuliersV2 from "@/pages/v2/Particuliers";
import ComparerOffresV2 from "@/pages/v2/ComparerOffres";
import ChangerFournisseurV2 from "@/pages/v2/ChangerFournisseur";
import ComprendreFactureV2 from "@/pages/v2/ComprendreFacture";
import DemenagementV2 from "@/pages/v2/Demenagement";
import AProposV2 from "@/pages/v2/APropos";
import FaqV2 from "@/pages/v2/Faq";
import ConfidentialiteV2 from "@/pages/v2/Confidentialite";
import CookiesV2 from "@/pages/v2/Cookies";
import ContactV2 from "@/pages/v2/Contact";
import FournisseursPartenairesV2 from "@/pages/v2/FournisseursPartenaires";
import PrimesCeeV2 from "@/pages/v2/PrimesCee";
import SecteurPrimesCeeV2 from "@/pages/v2/SecteurPrimesCee";
import { RedirectSecteurCee } from "@/pages/v2/SecteurPrimesCee";
import PrimesCeeProV2 from "@/pages/v2/PrimesCeePro";
import FournisseursPartenairesPartV2 from "@/pages/v2/FournisseursPartenairesPart";
import VeilleMarchePartV2 from "@/pages/v2/VeilleMarchePart";
import VeilleMarcheV2 from "@/pages/v2/VeilleMarche";
import ArticleV2 from "@/pages/v2/Article";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground" data-testid="auth-loading">
                Chargement de votre espace…
            </div>
        );
    }
    if (!user) return <Navigate to="/connexion" replace />;
    return children;
}

function AdminRoute({ children }) {
    const { user, loading, hasPerm } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground" data-testid="auth-loading">
                Chargement de votre espace…
            </div>
        );
    }
    // Non connecté → page de connexion
    if (!user) return <Navigate to="/connexion" replace />;
    // Connecté mais pas admin → renvoyé à l'accueil
    if (user.role !== "admin") return <Navigate to="/" replace />;
    // Admin mais sans la permission du module visé par l'URL → repli sur le
    // premier module autorisé (jamais de page blanche). "Mon compte" n'a pas
    // de perm et reste donc toujours accessible.
    const mod = findActiveModule(location.pathname);
    if (mod?.perm && !hasPerm(mod.perm)) {
        const fallback = getFirstAllowedModulePath(hasPerm);
        if (fallback !== location.pathname) return <Navigate to={fallback} replace />;
    }
    return children;
}

function AppLayout() {
    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            {/* Réserve l'espace minimal du header flottant */}
            <main className="flex-1 pt-20 md:pt-20"><Outlet /></main>
            <FooterV2 />
            {/* Chatbot Emma — bulle flottante bas-droite sur toutes les pages publiques */}
            <ChatbotWidget />
        </div>
    );
}

// Shell du site public 2.0 (Header/Footer Energy Market)
function PublicV2Layout() {
    return (
        <div className="min-h-screen flex flex-col">
            {/* Écran de choix Particulier / Professionnel à l'arrivée sur "/" */}
            <AudienceGate />
            <HeaderV2 />
            <main className="flex-1 pt-20"><Outlet /></main>
            <FooterV2 />
            <ChatbotWidget />
        </div>
    );
}

function App() {
    return (
        <PasswordGate>
            <ThemeProvider />
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        {/* --- Site public 2.0 (shell Energy Market) --- */}
                        <Route element={<PublicV2Layout />}>
                            <Route path="/" element={<AProposV2 />} />
                            <Route path="/professionnels" element={<ProfessionnelsV2 />} />
                            <Route path="/professionnels/optimisation-des-contrats" element={<OptimisationContratsV2 />} />
                            <Route path="/professionnels/anticipation-des-contrats" element={<RenegociationContratsV2 />} />
                            <Route path="/professionnels/renegociation-des-contrats" element={<Navigate to="/professionnels/anticipation-des-contrats" replace />} />
                            <Route path="/professionnels/gestion-multi-sites" element={<GestionMultiSitesV2 />} />
                            <Route path="/particuliers" element={<ParticuliersV2 />} />
                            <Route path="/particuliers/comparer-les-offres" element={<ComparerOffresV2 />} />
                            <Route path="/particuliers/changer-de-fournisseur" element={<ChangerFournisseurV2 />} />
                            <Route path="/particuliers/comprendre-sa-facture" element={<ComprendreFactureV2 />} />
                            <Route path="/particuliers/demenagement" element={<DemenagementV2 />} />
                            <Route path="/a-propos" element={<AProposV2 />} />
                            <Route path="/faq" element={<FaqV2 />} />
                            <Route path="/contact" element={<ContactV2 />} />
                            <Route path="/primes-cee" element={<PrimesCeeV2 />} />
                            <Route path="/professionnels/primes-cee" element={<PrimesCeeProV2 />} />
                            <Route path="/primes-cee/secteur/:slug" element={<RedirectSecteurCee />} />
                            <Route path="/professionnels/primes-cee/secteur/:slug" element={<SecteurPrimesCeeV2 />} />
                            <Route path="/fournisseurs-partenaires" element={<FournisseursPartenairesV2 />} />
                            <Route path="/particuliers/fournisseurs-partenaires" element={<FournisseursPartenairesPartV2 />} />
                            <Route path="/veille-marche" element={<VeilleMarcheV2 />} />
                            <Route path="/particuliers/veille-marche" element={<VeilleMarchePartV2 />} />
                            <Route path="/veille-marche/:slug" element={<ArticleV2 />} />
                            <Route path="/politique-de-confidentialite" element={<ConfidentialiteV2 />} />
                            <Route path="/gestion-des-cookies" element={<CookiesV2 />} />
                        </Route>

                        {/* --- App métier + admin (shell live conservé) --- */}
                        <Route element={<AppLayout />}>
                        <Route path="/comparateur" element={<Comparator />} />
                        <Route path="/fournisseurs" element={<Suppliers />} />
                        <Route path="/fournisseurs/comparer" element={<SuppliersCompare />} />
                        <Route path="/fournisseurs/:slug" element={<SupplierDetail />} />
                        <Route path="/concurrents" element={<Navigate to="/fournisseurs" replace />} />
                        <Route path="/devis" element={<QuoteForm />} />
                        <Route path="/pro/cee-solaire" element={<CEESolaire />} />
                        <Route path="/isolation-pac" element={<IsolationPAC />} />
                        <Route path="/changer-fournisseur" element={<Navigate to="/comparateur?audience=b2c" replace />} />
                        <Route path="/mentions-legales" element={<LegalPage slug="mentions" />} />
                        <Route path="/confidentialite" element={<LegalPage slug="privacy" />} />
                        <Route path="/cgu" element={<LegalPage slug="cgu" />} />
                        <Route path="/rgpd" element={<LegalPage slug="rgpd" />} />
                        </Route>

                        {/* --- Admin : layout dédié avec barre latérale (sans header/footer public) --- */}
                        <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
                        <Route path="/admin" element={<Navigate to="/admin/analytics" replace />} />
                        <Route path="/admin/devis" element={<AdminQuotes />} />
                        <Route path="/admin/offres-partenaires" element={<AdminPartnerOffers />} />
                        <Route
                            path="/admin/cee-leads"
                            element={
                                <AdminRoute>
                                    <AdminCEELeads />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics-ia"
                            element={
                                <AdminRoute>
                                    <AdminAnalyticsAI />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics"
                            element={
                                <AdminRoute>
                                    <AdminAnalyticsGlobal />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/mail"
                            element={
                                <AdminRoute>
                                    <AdminMail />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/compte"
                            element={
                                <AdminRoute>
                                    <AdminAccount />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/audit"
                            element={
                                <AdminRoute>
                                    <AdminAudit />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/notifications"
                            element={
                                <AdminRoute>
                                    <AdminNotifications />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/whatsapp"
                            element={
                                <AdminRoute>
                                    <AdminWhatsApp />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/isolation-leads"
                            element={
                                <AdminRoute>
                                    <AdminIsolationLeads />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/callbacks"
                            element={
                                <AdminRoute>
                                    <AdminCallbacks />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/contact-messages"
                            element={
                                <AdminRoute>
                                    <AdminContactMessages />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/fournisseurs"
                            element={
                                <AdminRoute>
                                    <AdminSuppliers />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/utilisateurs"
                            element={<Navigate to="/admin/clients" replace />}
                        />
                        <Route
                            path="/admin/clients"
                            element={
                                <AdminRoute>
                                    <AdminClients />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/equipe"
                            element={
                                <AdminRoute>
                                    <AdminTeam />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/contenu"
                            element={
                                <AdminRoute>
                                    <AdminContent />
                                </AdminRoute>
                            }
                        />
                        </Route>

                        {/* --- Reste de l'app métier / auth --- */}
                        <Route element={<AppLayout />}>
                        <Route path="/connexion" element={<Login />} />
                        <Route path="/inscription" element={<Register />} />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<NotFound />} />
                        </Route>
                    </Routes>
                    <Toaster richColors position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </PasswordGate>
    );
}

export default App;
